import InventoryCount from "../../../classes/InventoryCount";
import { db } from "../../../boot/firebase";
import {
  isCountLineCounted,
  summarizeCount,
} from "../../../util/inventoryCount";
import { timestampToDate } from "../../../util/misc";
import { chunk } from "lodash";
import {
  collection,
  deleteDoc,
  doc,
  getCountFromServer,
  getDocs,
  increment,
  limit,
  orderBy,
  query,
  setDoc,
  startAfter,
  updateDoc,
  where,
  writeBatch,
} from "firebase/firestore";

// Firestore's cap on the writes in one batch.
const BATCH_LIMIT = 500;

/**
 * Converter for a count's line documents (inventoryCounts/{id}/lines/{lineID})
 * -- plain line data with its id and the count it belongs to. Line ids come
 * from the item and the location (makeCountLineId), so two counts of the same
 * shelf share them: the store keys a line by count and line.
 */
const LineConverter = {
  toFirestore(line) {
    return line;
  },
  fromFirestore(snapshot, options) {
    const data = snapshot.data(options);
    data.id = snapshot.id;
    data.countID = snapshot.ref.parent.parent.id;
    data.countedAt = timestampToDate(data.countedAt);
    return data;
  },
  getStateID(path) {
    const [, countID, , lineID] = path.split("/");
    return `${countID}/${lineID}`;
  },
};

// Open count sessions are what the list page needs to live-update (someone
// else's tally landing while you're both counting); there are only ever a
// handful at once. Completed sessions accumulate forever, so they are NOT
// part of the live listener -- they're fetched a page at a time by
// GET_INVENTORY_COUNTS_PAGE instead. Without this bound, LISTEN_INVENTORY_COUNTS
// would subscribe to (and keep in memory) every count session ever run.
const ACTIVE_COUNTS_LIMIT = 100;

export const GET_INVENTORY_COUNT = ({ dispatch }, id) => {
  return dispatch("GET_DOC", {
    converter: InventoryCount,
    path: InventoryCount.getPath(id),
    stateKey: "inventoryCounts",
    id,
  });
};

export const LISTEN_INVENTORY_COUNTS = (
  { dispatch },
  { next, error, unlisten } = {}
) => {
  return dispatch("LISTEN_COLLECTION", {
    converter: InventoryCount,
    path: InventoryCount.getPath(),
    stateKey: "inventoryCounts",
    // Bound to open sessions only -- see ACTIVE_COUNTS_LIMIT above.
    conditions: ["status", "==", "active"],
    limitCount: ACTIVE_COUNTS_LIMIT,
    next,
    error,
    unlisten,
  });
};

/**
 * One-time, paginated fetch of COMPLETED count sessions for the history view.
 * Deliberately not a live listener (see LISTEN_INVENTORY_COUNTS above) --
 * history is read-only, so a page-at-a-time query is enough and keeps an
 * unbounded number of old sessions out of the live store/listener.
 */
export const GET_INVENTORY_COUNTS_PAGE = async (
  {},
  { startAfterDoc = null, limitCount = 25 } = {}
) => {
  const baseQuery = query(
    collection(db, InventoryCount.getPath()).withConverter(InventoryCount),
    where("status", "==", "complete"),
    orderBy("created", "desc")
  );
  const pageConstraints = [limit(limitCount)];
  if (startAfterDoc) {
    pageConstraints.push(startAfter(startAfterDoc));
  }

  const [countSnap, snapshot] = await Promise.all([
    getCountFromServer(baseQuery),
    getDocs(query(baseQuery, ...pageConstraints)),
  ]);

  return {
    docs: snapshot.docs.map((d) => d.data()),
    lastDoc: snapshot.docs[snapshot.docs.length - 1] || null,
    count: countSnap.data().count,
  };
};

export const LISTEN_INVENTORY_COUNT = (
  { dispatch },
  { id, next, error, unlisten }
) => {
  return dispatch("LISTEN_DOC", {
    converter: InventoryCount,
    path: InventoryCount.getPath(id),
    stateKey: "inventoryCounts",
    id,
    next,
    error,
    unlisten,
  });
};

/**
 * A count's lines, for the count page. Only that page needs them -- the list
 * reads the counts' own counters -- so it listens while it's open.
 */
export const LISTEN_INVENTORY_COUNT_LINES = (
  { dispatch },
  { id, next, error }
) => {
  return dispatch("LISTEN_COLLECTION", {
    converter: LineConverter,
    path: InventoryCount.getLinePath(id),
    stateKey: "inventoryCountLines",
    next,
    error,
  });
};

/**
 * Create a new count session with its frozen expected-line snapshot, one
 * document per line under inventoryCounts/{id}/lines. The writes go a batch
 * at a time, in order: the count is "creating" until every line is written
 * and only then made "active". A create that stops part-way (a failed batch,
 * a closed tab) leaves a "creating" count that neither tab of the list shows
 * and that the page and the rules won't let anyone count. Returns the new
 * document id.
 */
export const CREATE_INVENTORY_COUNT = async (
  { state },
  { title, scope, lines }
) => {
  const ref = doc(collection(db, InventoryCount.getPath()));
  const now = new Date();
  const entries = Object.entries(lines || {});
  const writes = [
    (batch) =>
      batch.set(ref, {
        title: title || "",
        status: "creating",
        scope: {
          warehouses: (scope && scope.warehouses) || [],
          includeVehicles: Boolean(scope && scope.includeVehicles),
        },
        lineCount: entries.length,
        countedCount: 0,
        reconciliation: null,
        notes: "",
        created: now,
        createdBy: state.user.id,
        completed: null,
        completedBy: null,
        modified: now,
        modifiedBy: state.user.id,
      }),
    ...entries.map(
      ([lineID, line]) =>
        (batch) =>
          batch.set(doc(db, InventoryCount.getLinePath(ref.id, lineID)), line)
    ),
  ];
  for (const group of chunk(writes, BATCH_LIMIT)) {
    const batch = writeBatch(db);
    group.forEach((write) => write(batch));
    await batch.commit();
  }
  await updateDoc(ref, { status: "active" });
  return ref.id;
};

export const UPDATE_INVENTORY_COUNT = ({ state }, { id, changes }) => {
  return setDoc(
    doc(db, InventoryCount.getPath(id)),
    { ...changes, modified: new Date(), modifiedBy: state.user.id },
    { merge: true }
  );
};

/**
 * Write one line. Only that line's document is written, so two people
 * counting different shelves of the same session never touch the same
 * document -- unless an expected line starts or stops counting: then the
 * count's countedCount moves with it in the same batch, so the list's
 * progress needs no lines. `counted` is whether the line is counted after
 * this write, and `wasCounted` whether it was before, as the page last set
 * it: a tap can come before the page has re-rendered the one before it, so
 * the `line` it was rendered from may be behind.
 */
const writeCountLine = (state, { id, line, changes, counted, wasCounted }) => {
  const batch = writeBatch(db);
  batch.update(doc(db, InventoryCount.getLinePath(id, line.id)), changes);
  const before =
    wasCounted === undefined ? isCountLineCounted(line) : wasCounted;
  const delta = line.extra ? 0 : Number(counted) - Number(before);
  if (delta) {
    batch.update(doc(db, InventoryCount.getPath(id)), {
      countedCount: increment(delta),
      modified: new Date(),
      modifiedBy: state.user.id,
    });
  }
  return batch.commit();
};

/**
 * Record (or overwrite) the counted quantity for a line: ticked boxes (box n
 * means n found) or a typed total. Null returns the line to uncounted. A
 * total, not an increment: two people ticking the same line at the same
 * moment can overwrite each other, which counters splitting shelves avoids,
 * but nothing is ever counted twice.
 */
export const SET_INVENTORY_COUNT_LINE_QTY = (
  { state },
  { id, line, countedQty, wasCounted }
) => {
  const cleared = countedQty === null || countedQty === undefined;
  return writeCountLine(state, {
    id,
    line,
    counted: !cleared,
    wasCounted,
    changes: {
      countedQty: cleared ? null : Number(countedQty),
      countedBy: cleared ? null : state.user.id,
      countedAt: cleared ? null : new Date(),
    },
  });
};

/** Add an "extra" line for stock found that the snapshot didn't expect. */
export const ADD_INVENTORY_COUNT_LINE = ({}, { id, lineId, line }) => {
  return setDoc(doc(db, InventoryCount.getLinePath(id, lineId)), {
    ...line,
    extra: true,
  });
};

/**
 * Complete a count: read its lines and freeze their reconciliation onto it,
 * with its progress recounted from them, so the history list needs no lines.
 */
export const COMPLETE_INVENTORY_COUNT = async ({ state }, { id }) => {
  const snapshot = await getDocs(
    collection(db, InventoryCount.getLinePath(id))
  );
  const reconciliation = summarizeCount(
    snapshot.docs.map((line) => line.data()),
    true
  );
  const now = new Date();
  return setDoc(
    doc(db, InventoryCount.getPath(id)),
    {
      status: "complete",
      lineCount: reconciliation.expectedLines,
      countedCount: reconciliation.countedLines,
      reconciliation,
      completed: now,
      completedBy: state.user.id,
      modified: now,
      modifiedBy: state.user.id,
    },
    { merge: true }
  );
};

export const REOPEN_INVENTORY_COUNT = ({ state }, { id }) => {
  return setDoc(
    doc(db, InventoryCount.getPath(id)),
    {
      status: "active",
      reconciliation: null,
      completed: null,
      completedBy: null,
      modified: new Date(),
      modifiedBy: state.user.id,
    },
    { merge: true }
  );
};

/**
 * Delete a count. Its lines don't go with it, so they go first, a batch at a
 * time; if that stops part-way the count is still there to delete again.
 */
export const DELETE_INVENTORY_COUNT = async ({ commit }, id) => {
  const lines = await getDocs(collection(db, InventoryCount.getLinePath(id)));
  for (const group of chunk(lines.docs, BATCH_LIMIT)) {
    const batch = writeBatch(db);
    group.forEach((line) => batch.delete(line.ref));
    await batch.commit();
  }
  await deleteDoc(doc(db, InventoryCount.getPath(id)));
  commit("REMOVE", { stateKey: "inventoryCounts", id });
};
